import { useSelector } from "react-redux";
import { getMainState } from "../store/components/Main.slice";
import { MdSpaceDashboard, MdLayers, MdPublic, MdLockOutline } from "react-icons/md";
import { FaUsersLine } from "react-icons/fa6";
import { FcComboChart } from "react-icons/fc";

import BGRight from "../images/svg/BGRight.svg";
import BGLeft from "../images/svg/BGLeft.svg";
import { useMemo, useState } from "react";
import OneInsightModal, { isHardcodedUser } from "./OneInsightModal";

const legacyItems = [
  {
    header: "Discovery",
    appName: "ONE Insight",
    appId: 35,
    appCode: "oneinsightv5",
    appDescription:
      "Turn complex behaviours into actionable intelligence and compelling audience stories.",
    icon: <MdSpaceDashboard />,
  },
  {
    header: "Set Up",
    appName: "ONE Audience",
    appId: 2,
    appCode: "onemarketaudiencebuilder",
    appDescription: "Access your audience packages here created before.",
    icon: <FaUsersLine />,
  },
  {
    header: "Campaign Ops",
    appName: "ONE Market",
    appId: 8,
    appCode: "onemarketdsp",
    appDescription: "Activate and manage your digital ad campaigns.",
    icon: <FcComboChart />,
  },
];

const platformItems = [
  {
    header: "Platform",
    appName: "ONE Platform 2.0",
    appId: 35,
    appCode: "oneplatform2",
    icon: <MdLayers />,
    isBeta: true,
    isUSAOnly: true,
    isPlatform: true,
    url: "https://one.sqreemtech.com/v2/"
  },
];

export const oneinsightLinkObj = {
  consumer: "https://oneinsight.sqreemtech.com/",
  healthcare: "http://onehealth.sqreemtech.com/",
  government: "https://oneinsight.sqreemtech.com/gov/",
};

function AppList() {
  const { appList, v2AppList, oneInsightPerm } = useSelector(getMainState);
  const [isModalOIOpen, setIsModalOIOpen] = useState(false);

  const legacyApps = useMemo(() => {
    return legacyItems.map((item) => {
      const filteredItem = appList.find((app) => item.appId === parseInt(app.id));
      return {
        ...item,
        disabled: !filteredItem || !filteredItem?.isActive,
        appName: filteredItem?.name || item.appName,
        description: filteredItem?.description || item.appDescription,
        url: filteredItem?.url,
      };
    });
  }, [appList]);

  const platformApps = useMemo(() => {
    return platformItems.map((item) => {
      const filteredItem = v2AppList.find((app) => item.appId === parseInt(app.id));
      return {
        ...item,
        disabled: !filteredItem,
      };
    });
  }, [v2AppList]);

  const onItemClick = (e, appId) => {
    switch (appId) {
      case 35: {
        if (!isHardcodedUser()) {
          if (oneInsightPerm.length === 1) {
            break;
          }
        }
        e.preventDefault();
        setIsModalOIOpen(true);
        break;
      }
      default:
        break;
    }
  };

  const handleOIChoose = (code) => {
    window.open(oneinsightLinkObj[code], "_blank");
  };

  const renderCard = ({
    header,
    appName,
    icon,
    isPlatform,
    description,
    url: givenUrl,
    disabled: givenDisabled,
    appId,
    isBeta,
    isUSAOnly,
  }) => {
    let disabled = givenDisabled;
    let url = givenUrl;
    if (appId === 35) {
      if (!oneInsightPerm.length) disabled = true;
      else if (oneInsightPerm.length === 1)
        url = oneinsightLinkObj[oneInsightPerm[0]];
    }

    return (
      <a
        key={appId}
        href={url}
        target="_blank"
        rel="noreferrer"
        className={`app-list-item ani-zoomIn${disabled ? " disabled" : ""}`}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          if (isPlatform) return;
          onItemClick(e, appId);
        }}
      >
        <div className="app-list-item-top">
          <div className="app-list-item-icon">{icon}</div>
          <div className="app-list-item-header">{header}</div>
        </div>

        <div className="app-list-item-name">
          {appName}
          {isBeta && <span className="app-list-item-beta">BETA</span>}
        </div>

        {isBeta ? (
          <div className="app-list-item-desc">
            <strong>One Platform 2.0</strong> is now live. Experience the next
            generation of AI-driven audience intelligence.
          </div>
        ) : (
          <div className="app-list-item-desc">{description}</div>
        )}

        {isUSAOnly && (
          <div className="app-list-item-usa">
            <MdPublic />
            <span>USA only · Other regions coming soon</span>
          </div>
        )}

        <div className={`app-list-item-btn${disabled ? " disabled" : ""}`}>
          {disabled ? (
            <><MdLockOutline /> No Access</>
          ) : (
            <>Launch {appName} →</>
          )}
        </div>
      </a>
    );
  };

  return (
    <div className="app-list d-flex d-flex-col justify-content-center align-items-center h-full">
      <h2 className="app-list-title">ONE</h2>
      <p className="app-list-desc">
        The most powerful end-to-end AI-driven audience intelligence operating
        system for smarter audience planning and targeting.
      </p>

      <div className="app-list-container">
        <div className="app-list-section">
          <div className="app-list-section-label">Legacy</div>
          <div className="app-list-section-cards">
            {legacyApps.map(renderCard)}
          </div>
        </div>

        <div className="app-list-divider" />

        <div className="app-list-section">
          <div className="app-list-section-label">ONE Platform</div>
          <div className="app-list-section-cards">
            {platformApps.map(renderCard)}
          </div>
        </div>
      </div>

      <OneInsightModal
        isVisible={isModalOIOpen}
        onOk={handleOIChoose}
        onCancel={() => setIsModalOIOpen(false)}
      />

      <img alt="" className="app-list-bg app-list-bg--left" src={BGLeft} />
      <img alt="" className="app-list-bg app-list-bg--right" src={BGRight} />
    </div>
  );
}

export default AppList;
